import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";

/**
 * Logs in to the client's personal area at the provider (not the agent
 * portal), on the agent's own computer, through the local REV&U downloader
 * ("Log in as a client").
 *
 * The provider sends an OTP SMS to the CLIENT's phone; the client reads it to
 * the agent, who types it in the browser. So a confirmation dialog is shown
 * first, then the revu:// link is opened: the downloader shows its own
 * confirmation window and opens the login in a separate Chrome window.
 *
 * Shown only for providers whose downloader module has login_as_client.
 */

// handler_code -> provider key in the downloader (links.py PROVIDER_NAMES).
const PROVIDER_BY_HANDLER_CODE: Record<string, { key: string; name: string }> = {
  "512065202": { key: "meitav", name: "מיטב" },
  "514956465": { key: "more", name: "מור" },
  "513611509": { key: "yelin", name: "ילין לפידות" },
  "512244146": { key: "clal", name: "כלל" },
  "520024647": { key: "clal", name: "כלל" },
  "520023185": { key: "phoenix", name: "הפניקס" },
  "513026484": { key: "phoenix", name: "הפניקס" },
  "513173393": { key: "altshuler", name: "אלטשולר" },
  "511880460": { key: "analyst", name: "אנליסט" },
  "512267592": { key: "harel", name: "הראל" },
  "520004078": { key: "harel", name: "הראל" },
  "512245812": { key: "menora", name: "מנורה מבטחים" },
  "520042540": { key: "menora", name: "מנורה מבטחים" },
  "513621110": { key: "infinity", name: "אינפיניטי" },
  "512237744": { key: "migdal", name: "מגדל" },
  "520004896": { key: "migdal", name: "מגדל" },
  "520042177": { key: "hachshara", name: "הכשרה" },
  "520030677": { key: "ayalon", name: "איילון" },
};

/** 05XXXXXXXX, or undefined when the number is not an Israeli mobile (the downloader then asks for it) */
const normalizeMobile = (phone?: string): string | undefined => {
  let digits = (phone ?? "").replace(/\D/g, "");
  if (digits.startsWith("972")) digits = "0" + digits.slice(3);
  return /^05\d{8}$/.test(digits) ? digits : undefined;
};

interface LoginAsClientButtonProps {
  handlerCode?: string;
  /** The client's ID number (product.client.client_id) */
  clientId?: string;
  /** The client's mobile phone - optional, the downloader asks for it when missing */
  phone?: string;
  /** Above the drawer when it is opened from a dialog */
  zIndex?: number;
}

const LoginAsClientButton: React.FC<LoginAsClientButtonProps> = ({ handlerCode, clientId, phone, zIndex }) => {
  const [open, setOpen] = useState(false);

  const provider = handlerCode ? PROVIDER_BY_HANDLER_CODE[handlerCode] : undefined;
  const id = (clientId ?? "").replace(/\D/g, "");

  if (!provider || !id) return null;

  const mobile = normalizeMobile(phone);
  const link =
    `revu://login-client?provider=${provider.key}&client=${encodeURIComponent(id)}` +
    (mobile ? `&phone=${mobile}` : "");

  const handleConfirm = () => {
    setOpen(false);
    window.location.href = link;
  };

  return (
    <>
      <Tooltip title={`כניסה לאזור האישי של הלקוח באתר ${provider.name} (דרך תוכנת REV&U במחשב)`}>
        <IconButton size="small" onClick={() => setOpen(true)}>
          <LoginIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} dir="rtl" maxWidth="xs" fullWidth sx={zIndex ? { zIndex: zIndex + 1 } : undefined}>
        <DialogTitle>כניסה לאזור האישי של הלקוח</DialogTitle>
        <DialogContent>
          <DialogContentText>
            כניסה לאזור האישי של הלקוח באתר {provider.name} תשלח ללקוח קוד הזדהות (OTP) ב-SMS לטלפון שלו.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>האם להמשיך?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} autoFocus>
            לא
          </Button>
          <Button onClick={handleConfirm} variant="contained">
            כן
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoginAsClientButton;
